import { PropsWithChildren } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { UserPermissions } from '@ravago/shared/page-data/models/config';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Sublevel } from '@ravago/shared/radiance/components/sublevel/components/Sublevel/Sublevel';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { usePathname, useSearchParams } from 'next/navigation';

interface Props extends PropsWithChildren {
   productNotFound: boolean;
   isTailCatalog: boolean;
   businessContextEmailAddress: string | undefined | null;
   userPermissions: UserPermissions;
}

export const RequestForQuotationContentGuard = ({
   productNotFound,
   isTailCatalog,
   businessContextEmailAddress,
   userPermissions,
   children
}: Props) => {
   const { user, login } = useAuthentication();
   const translate = useTranslations();

   const searchParams = useSearchParams();
   const pathname = usePathname();

   const handleLogin = () => login(`${pathname}?${searchParams.toString()}`);

   if (productNotFound && !isTailCatalog) {
      return <Alert variant="error" headline={translate('request-for-quotation.product-not-available')} />;
   }

   if (isTailCatalog) {
      return (
         <Box direction="column" gap="lg">
            <Alert variant="neutral" headline={translate('request-for-quotation.quoting-not-available')}>
               <Typography>
                  {translate('request-for-quotation.tail-catalog-product-not-available')}{' '}
                  {businessContextEmailAddress && (
                     <Link color="light" href={`mailto:${businessContextEmailAddress}`}>
                        <Typography color="tertiary">{businessContextEmailAddress}</Typography>
                     </Link>
                  )}
                  .
               </Typography>
            </Alert>
         </Box>
      );
   }

   if (!user && !userPermissions.hasAnonymousPermission) {
      return (
         <Typography component="b" variant="body">
            {translate('request-for-quotation.anonymous-user')}
         </Typography>
      );
   }

   if (user?.groups?.includes(UserGroups.digitalPlatformPendingVerification) && !userPermissions.hasPendingPermission) {
      return (
         <Typography component="b" variant="body">
            {translate('request-for-quotation.pending-user')}
         </Typography>
      );
   }

   if (user?.groups?.includes(UserGroups.digitalPlatformGuest) && !userPermissions.hasGuestPermission) {
      return (
         <Typography component="b" variant="body">
            {translate('request-for-quotation.guest-user')}
         </Typography>
      );
   }

   if (!user && userPermissions.hasAnonymousPermission) {
      return (
         <Box direction="column">
            <Sublevel>
               <Box direction="column" gap="xs">
                  <Typography component="span" variant="body">
                     {translate('request-for-quotation.login-message')}
                  </Typography>
                  <Box>
                     {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                     <Link onClick={handleLogin} color="dark">
                        {translate('request-for-quotation.login')}
                     </Link>
                  </Box>
               </Box>
            </Sublevel>
            {children}
         </Box>
      );
   }

   return children;
};
